// src/data/storyThreadsModulesMetadata.js — the eight core modules at a glance.
//
// Title, subtitle, description and achievement must match the module files:
// the modules page spreads these over the module, so these are what shows.
//
// Titles, levels, durations and unlock order, shared by the modules page, the
// home page and the dashboard. The sessions themselves live in data/modules/;
// the modules page merges the two when a module is opened. (This list used to
// sit inside StoryThreadsModulesPage, and the page handed it to the module
// viewer on its own, so modules 1–8 opened with no sessions in them.)
import { Castle, Compass, Gem, GraduationCap, MessageCircle, Search, Users, Zap } from 'lucide-react';

export const storyThreadsModulesData = [
  {
    id: 1,
    title: 'Why Family Stories Matter',
    subtitle: 'What the research actually says about family stories (and how to use them well)',
    description: "Every family has stories - about hard times, good times and how people got through. Researchers have found that young people who know more of their family's stories tend to do better on a range of wellbeing measures. This module shows you what that research does (and doesn't) say, and gives you practical ways to notice, ask for and tell family stories well.",
    level: 'Foundation',
    category: 'Story Science & Discovery',
    duration: '25 min',
    icon: Zap,
    color: 'from-sky-500 to-cyan-500',
    emoji: '⚡',
    image: '/images/story-threads-family-superpower-720px.png',
    imageAlt: 'Young person discovering glowing family story connections with brain and heart imagery',
    vibeCheck: 'What does the research on family stories actually say?',
    targetAreas: ['Family Stories Research', 'Family Connection', 'Story Styles', 'Real-Talk Stories'],
    keySkills: ['Real-Talk Story Formula', 'Family Story Styles', 'Spotting story moments'],
    prerequisites: [],
    coolFactor: 'Know what the research says, and what it does not',
    series: 'Foundation',
    xpReward: 250,
    achievement: 'Story Spotter ⚡',
  },
  {
    id: 2,
    title: 'Become the Family Story Detective',
    subtitle: 'Find the stories hiding in plain sight',
    description: 'Every family has stories that rarely get told - about courage, love, mistakes and getting through. This module is about finding them: who to ask, what to ask, and how to listen when they start talking.',
    level: 'Foundation', 
    category: 'Story Discovery & Exploration',
    duration: '28 min',
    icon: Search,
    color: 'from-amber-500 to-orange-600',
    emoji: '🕵️',
    image: '/images/story-threads-story-detective-720px.png',
    imageAlt: 'Young person with magnifying glass discovering glowing family story connections and hidden treasures',
    vibeCheck: 'Ready to find the stories nobody has asked for yet?',
    targetAreas: ['Finding Stories', 'Asking Good Questions', 'Hidden Stories', 'Generational Connections'],
    keySkills: ['Finding hidden stories', 'Interviewing an older relative', 'Family story mapping'],
    prerequisites: [1],
    coolFactor: 'Like a detective, but for your own family',
    series: 'Foundation',
    xpReward: 275,
    achievement: 'Family Story Detective 🕵️',
  },
  {
    id: 3,
    title: 'The Five Story Ingredients',
    subtitle: 'A simple recipe for telling family stories that feel honest and complete',
    description: "Here's a practical framework: five ingredients that help a family story feel honest, balanced and useful. Plenty of family stories leave one or two out, which can make them fall flat or leave people feeling worse. You'll learn a recipe for turning family experiences into stories that connect people - without changing what happened.",
    level: 'Foundation',
    category: 'Story Construction & Mastery',
    duration: '30 min',
    icon: Gem,
    color: 'from-purple-500 to-pink-600',
    emoji: '🧪',
    image: '/images/story-threads-story-alchemist-720px.png',
    imageAlt: 'Young person in lab coat mixing glowing story ingredients in magical laboratory setting',
    vibeCheck: 'Ready to see what makes a family story work?',
    targetAreas: ['Story Craft', 'Story Structure', 'Honest Stories', 'Connection Building'],
    keySkills: ['The five story ingredients', 'Spotting what a story is missing', 'Story building workshop'],
    prerequisites: [1, 2],
    coolFactor: 'A recipe you can check any story against',
    series: 'Foundation',
    xpReward: 300,
    achievement: 'Story Alchemist 🧪',
  },
  {
    id: 4,
    title: 'Become the Family Conversation Catalyst',
    subtitle: 'Help family talk move past small talk and awkward silences',
    description: "You know that feeling when family conversations get stuck in surface-level small talk or uncomfortable silence? This module gives you practical ways to nudge family gatherings towards more meaningful conversation. You won't be able to change every dynamic, but small shifts in the questions you ask can make a real difference.",
    level: 'Application',
    category: 'Conversation Facilitation & Social Mastery',
    duration: '32 min',
    icon: MessageCircle,
    color: 'from-cyan-500 to-blue-600',
    emoji: '💬',
    image: '/images/story-threads-conversation-catalyst-720px.png',
    imageAlt: 'Young person facilitating animated family conversation with glowing connection lines between family members',
    vibeCheck: 'Ready to get past small talk at home?',
    targetAreas: ['Starting Conversations', 'Family Gatherings', 'Asking Better Questions', 'Connection Building'],
    keySkills: ['Conversation starters', 'Bridging between generations', 'Family meeting ideas'],
    prerequisites: [1, 2, 3],
    coolFactor: 'Better questions, better conversations',
    series: 'Foundation',
    xpReward: 325,
    achievement: 'Conversation Catalyst 💬',
  },
  {
    id: 5,
    title: 'Story Adaptation Across Ages',
    subtitle: 'Tell the same family story in ways that work for different ages',
    description: 'The same family story can frighten a 6-year-old, bore a teenager, or feel patronising to an adult - a lot depends on how you tell it. Learn practical ways to adapt family stories for different ages and stages, so more people in your family can connect with them.',
    level: 'Application',
    category: 'Age-Appropriate Communication & Translation',
    duration: '30 min',
    icon: Users,
    color: 'from-emerald-500 to-teal-600',
    emoji: '👥',
    image: '/images/story-threads-family-translator-720px.png',
    imageAlt: 'Young person surrounded by family members of all ages, with story bubbles adapted for each generation',
    vibeCheck: 'Ready to tell one story three different ways?',
    targetAreas: ['Developmental Awareness', 'Story Translation', 'Cross-Generational Communication', 'Family Harmony'],
    keySkills: ['Adapting stories by age', 'Telling stories to young kids', 'Talking with teens'],
    prerequisites: [1, 2, 3, 4],
    coolFactor: 'One story, told right for every age',
    series: 'Foundation',
    xpReward: 350,
    achievement: 'Family Translator 👥',
  },
  {
    id: 6,
    title: 'Build Lasting Family Story Traditions',
    subtitle: 'Create storytelling traditions that can last',
    description: "You've practised the skills - now you can use them to build something that lasts. Design family storytelling traditions, set up simple ways to keep stories safe, and help shape how your family's stories get passed on.",
    level: 'Application',
    category: 'Legacy Building & Tradition Creation',
    duration: '35 min',
    icon: Castle,
    color: 'from-violet-600 to-purple-700',
    emoji: '🏰',
    image: '/images/story-threads-story-empire-720px.png',
    imageAlt: 'Young person designing grand family story castle with multiple towers representing different storytelling traditions',
    vibeCheck: 'Ready to start a family story tradition?',
    targetAreas: ['Traditions', 'Tradition Design', 'Keeping Stories Safe', 'Passing Stories On'],
    keySkills: ['STORY tradition framework', 'Keeping stories safe', 'Legacy project planning'],
    prerequisites: [1, 2, 3, 4, 5],
    coolFactor: 'Build something your family keeps doing',
    series: 'Foundation',
    xpReward: 400,
    achievement: 'Tradition Builder 🏰',
  },
  {
    id: 7,
    title: 'Navigate Tricky Family Conversations',
    subtitle: "When family situations get complex, you'll have ways to help - and know when to get support",
    description: 'Life happens. Families face mental health challenges, major transitions, conflicts, and loss. When things get complicated, you want to be the family member who can navigate difficult conversations with care and compassion. These are storytelling approaches for complex family situations - and for recognising when a situation needs more than you can give.',
    level: 'Advanced',
    category: 'Complex Situation Navigation & Crisis Communication',
    duration: '32 min',
    icon: Compass,
    color: 'from-indigo-600 to-purple-700',
    emoji: '🧭',
    image: '/images/story-threads-family-navigator-720px.png',
    imageAlt: 'Young person confidently navigating complex family situation with compass and supportive family connections',
    vibeCheck: 'Ready for the harder family conversations?',
    targetAreas: ['Hard Conversations', 'Mental Health Conversations', 'Conflict', 'Knowing When to Get Support'],
    keySkills: ['HOPE framework for mental health', 'BRIDGE framework for change', 'CONNECT framework for conflict'],
    prerequisites: [1, 2, 3, 4, 5, 6],
    coolFactor: 'Know what to say, and when to get help',
    series: 'Foundation',
    xpReward: 375,
    achievement: 'Family Navigator 🧭',
  },
  {
    id: 8,
    title: 'Become a Family Story Teacher',
    subtitle: "Share what you've learned about family storytelling with others",
    description: "You've worked through the foundations and some tricky situations. Now you can share what you've learned: help friends and others try family storytelling, build a bit of community around it, and spread connection beyond your own family.",
    level: 'Mastery',
    category: 'Teaching, Mentorship & Community Impact',
    duration: '35 min',
    icon: GraduationCap,
    color: 'from-rose-500 to-pink-600',
    emoji: '🎓',
    image: '/images/story-threads-story-master-720px.png',
    imageAlt: 'Young person teaching family storytelling to diverse group with connections spreading across community',
    vibeCheck: 'Ready to help someone else find their family stories?',
    targetAreas: ['Sharing Skills', 'Community', 'Mentoring', 'Helping Others'],
    keySkills: ['Teaching family story skills', 'GUIDE mentoring framework', 'Community story building'],
    prerequisites: [1, 2, 3, 4, 5, 6, 7],
    coolFactor: "Pass on what you've learned",
    series: 'Foundation',
    xpReward: 500,
    achievement: 'Story Mentor 🎓',
  },
];

// The Advanced Addon (modules 9–12) opens once all eight are finished.
export const advancedAddonModules = [
  { id: 9, title: 'Story Architecture' },
  { id: 10, title: 'Sensory Storytelling' },
  { id: 11, title: 'Performance Power' },
  { id: 12, title: 'Keeping Stories for Good' },
];

// Hand-off so "Start Module 1" and the dashboard's "Continue" open the module
// itself rather than the list. The modules page reads it once, on mount.
const OPEN_MODULE_KEY = 'story-threads-vibe_open_module';

export const openModule = (setCurrentView, id) => {
  try {
    sessionStorage.setItem(OPEN_MODULE_KEY, String(id));
  } catch { /* storage unavailable: the list still opens */ }
  setCurrentView('modules');
};

export const consumeOpenModule = () => {
  try {
    const raw = sessionStorage.getItem(OPEN_MODULE_KEY);
    sessionStorage.removeItem(OPEN_MODULE_KEY);
    const id = Number(raw);
    return Number.isInteger(id) && id > 0 ? id : null;
  } catch {
    return null;
  }
};
